import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Areas.css';

function useInView(options = {}) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(element);
        }
      },
      {
        threshold: 0.1,
        ...options,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isInView];
}

// Helper: "Columbus, Ohio" → "columbus-ohio"
const toSlug = (name) =>
  name
    .toLowerCase()
    .replace(/,\s*/g, '-')
    .replace(/\s+/g, '-');

const Areas = () => {
  const areas = [
    'Columbus, Ohio',
    'Dublin, Ohio',
    'Hilliard, Ohio',
    'Powell, Ohio',
    'Marysville, Ohio',
    'Reynoldsburg, Ohio',
    'Blacklick, Ohio',
    'Gahanna, Ohio',
    'Delaware, Ohio',
  ];

  const brands = [
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-1.jpg', alt: 'Weiser' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/kwiksetlogo446.jpg', alt: 'Kwikset' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/clopay-logo.jpg', alt: 'Clopay' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/amarr-logo.jpg', alt: 'Amarr' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/andersen-logo.jpg', alt: 'Andersen' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/jw-logo.jpg', alt: 'JELD-WEN' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/garaga4.jpg', alt: 'Garaga' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-14567667697.jpg', alt: 'Ridgeway' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-154447.jpg', alt: 'Steel Craft' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-145658634.jpg', alt: 'Loewen' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-1356658.jpg', alt: 'Richards-Wilcox' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-1466.jpg', alt: 'Ply Gem' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weise566857463.jpg', alt: 'Pollard' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-1346747546.jpg', alt: 'Vanfax' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-134565457346.jpg', alt: 'Dalmen Pro' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-1354668545.jpg', alt: 'Belron' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/weiserlogo-135675.jpg', alt: 'Bigfoot Door' },
    { src: 'https://fixone.ca/wp-content/uploads/2025/03/wei6765756346.jpg', alt: 'NordGlass' },
  ];

  const [leftRef, leftInView] = useInView();
  const [rightRef, rightInView] = useInView();

  return (
    <div style={{ backgroundColor: '#f8f9fa' }}>
      <div className="areas-scope">
        <section className="cs-section areas-serve-section">
          <div className="container-fluid px-3 px-md-4 px-lg-5">

            {/* Subtitle */}
            <div
              className={`areas-subtitle-container d-flex justify-content-lg-start justify-content-md-center justify-content-center ${
                leftInView ? 'areas-animate-fade-left' : 'areas-opacity-0'
              }`}
            >
              <span className="areas-subtitle">
                <span className="areas-dot"></span> Areas We Serve
              </span>
            </div>

            <div className="row g-4 g-lg-5 d-flex align-items-start">

              {/* Left Section */}
              <div className="col-lg-6" ref={leftRef}>
                <div className={leftInView ? 'areas-animate-fade-left' : 'areas-opacity-0'}>
                  <h2 className="cs-title areas-section-heading text-center text-lg-start">
                    We Serve <span className="areas-red">Across Central Ohio.</span>
                  </h2>

                  <div className=" d-flex justify-content-center justify-content-lg-start text-center text-lg-start">
                    <div className=" areas-heading-underline"></div>
                  </div>

                  <p className="cs-desc areas-intro-p text-center text-lg-start">
                    We proudly provide professional roofing services across Reynoldsburg, Ohio, and surrounding cities, including
                  </p>
                </div>

                <ul className="cs-content areas-list">
                  {areas.map((area, i) => (
                    <li
                      key={i}
                      className={leftInView ? 'areas-animate-fade-left' : 'areas-opacity-0'}
                      style={{ animationDelay: `${i * 0.05}s` }}
                    >
                      <Link
                        to={`/location/${toSlug(area)}`}
                        className="areas-link"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <span className="areas-icon">
                          <i className="bi bi-geo-alt-fill"></i>
                        </span>

                        <span className="areas-name">{area}</span>
                      </Link>
                    </li>
                  ))}
                </ul>

                <div
                  className={`areas-note mt-4 ${
                    leftInView ? 'areas-animate-fade-up' : 'areas-opacity-0'
                  }`}
                  style={{ animationDelay: '0.6s' , backgroundColor: '#f8f9fa'}} 
                >
                  <p>
                    <strong>
                      Ultimates Roofing proudly serves homeowners and businesses across Reynoldsburg, Ohio and surrounding communities.
                    </strong>
                  </p>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-lg-6 areas-brands-col" ref={rightRef}>
                <div className={rightInView ? 'areas-animate-fade-right' : 'areas-opacity-0'}>
                  <h2 className="areas-brands-heading text-center text-md-center text-lg-start">
                    Brands <span className="areas-red">We Trust</span>
                  </h2>
<div className='d-flex justify-content-center justify-content-md-center justify-content-lg-start '>
   <div className="areas-heading-underline"></div>
</div>
                 
                </div>

                <div className="areas-brands-grid">
                  {brands.map((brand, i) => (
                    <div
                      key={i}
                      className={`areas-brand-card ${
                        rightInView ? 'areas-animate-fade-up' : 'areas-opacity-0'
                      }`}
                      style={{ animationDelay: `${i * 0.05}s` }}
                    >
                      <img src={brand.src} alt={brand.alt} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Areas;